"use client";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { BsTwitterX } from "react-icons/bs";
import {
  LuCalendar,
  LuChevronDown,
  LuCircleDollarSign,
  LuGem,
  LuKeyRound,
  LuLandmark,
  LuScrollText,
  LuSmile,
  LuText,
  LuThumbsDown,
  LuThumbsUp,
  LuUser,
  LuUser as LuUser2,
  LuUsers as LuUsers2,
} from "react-icons/lu";

import {
  FilterBoolean,
  FilterDate,
  FilterMatchString,
  FilterNumeric,
  FilterNumericRangeWithTimePeriod,
  FiltersSet,
  FilterString,
} from "@/components/shared/filter-sections";
import { PopoverFilterSortActions } from "@/components/shared/popover-filter-sort-actions";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
  usePopoverDrawerContext,
} from "@/components/ui/popover-drawer";
import { WHALE_ICON } from "@/utils/constants";

import { DiscoverFilterFields } from "../discover-sort-constants";
import { useDiscoverContext } from "./discover-context";

export const DiscoverFilterPopover = ({
  isTimePeriodDisabled,
}: {
  isTimePeriodDisabled?: boolean;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const { setFiltersToLastSaved } = useDiscoverContext();

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      return;
    }

    if (!open && !submitting) {
      setFiltersToLastSaved();
    }
  }, [open, setFiltersToLastSaved, submitting]);

  return (
    <PopoverDrawer
      historyName="discover-filters"
      onOpenChange={setOpen}
      open={open}
      title="Discover filters"
    >
      <PopoverDrawerTrigger asChild>
        <Button variant="link" className="flex items-center gap-1 text-muted">
          <span className="text-xs">Filter</span>
          <LuChevronDown />
        </Button>
      </PopoverDrawerTrigger>
      <DiscoverFilterPopoverContent
        isTimePeriodDisabled={isTimePeriodDisabled}
        setSubmitting={setSubmitting}
      />
    </PopoverDrawer>
  );
};

const DiscoverFilterPopoverContent = ({
  isTimePeriodDisabled,
  setSubmitting,
}: {
  isTimePeriodDisabled?: boolean;
  setSubmitting: (submitting: boolean) => void;
}) => {
  const {
    onFiltersApply,
    findFilterByName,
    updateFilter,
    removeFilter,
    resetDiscoverFilters,
    isFilterActive,
    syncLastSavedFilters,
    isFilterDisabled,
  } = useDiscoverContext();
  const { close } = usePopoverDrawerContext();

  const methods = {
    findFilterByName,
    updateFilter,
    removeFilter,
    isFilterActive,
    isFilterDisabled,
    isTimePeriodDisabled,
  };

  const onSubmitClick = useCallback(() => {
    setSubmitting(true);
    syncLastSavedFilters();
    onFiltersApply();
    close();
  }, [onFiltersApply, close, syncLastSavedFilters, setSubmitting]);

  const onResetClick = useCallback(() => {
    setSubmitting(true);
    resetDiscoverFilters();
    close();
  }, [close, resetDiscoverFilters, setSubmitting]);

  const checkIfSetIsDisabled = useCallback(
    (fields: DiscoverFilterFields[]) => {
      return fields.every((field) => isFilterDisabled(field));
    },
    [isFilterDisabled],
  );

  return (
    <PopoverDrawerContent
      align="start"
      side="bottom"
      drawerDescription="Filter discover users by profile, activity, wealth, and social metrics."
      className="
        custom-scrollbar w-screen overflow-y-auto p-6 pb-0
        shadow-xl md:max-h-[min(72vh,450px)] md:min-h-[450px]
        md:w-[600px] md:pb-6
      "
    >
      <Accordion type="single" collapsible>
        <div className="flex flex-col gap-5">
          <FiltersSet
            name="Profile"
            isSectionDisabled={checkIfSetIsDisabled([
              DiscoverFilterFields.publicKeyBase58Check,
              DiscoverFilterFields.username,
              DiscoverFilterFields.description,
              DiscoverFilterFields.joinedFocusDate,
            ])}
          >
            <FilterString<DiscoverFilterFields>
              {...methods}
              label="Public key"
              placeholder="Start typing a public key..."
              name={DiscoverFilterFields.publicKeyBase58Check}
              icon={<LuKeyRound />}
            />
            <FilterString<DiscoverFilterFields>
              {...methods}
              label="Username"
              placeholder="Start typing a username..."
              name={DiscoverFilterFields.username}
              icon={<LuUser />}
            />

            <FilterMatchString<DiscoverFilterFields>
              {...methods}
              label="Description"
              name={DiscoverFilterFields.description}
              icon={<LuText />}
            />
            <FilterDate<DiscoverFilterFields>
              {...methods}
              label="Joined Date"
              icon={<LuCalendar />}
              name={DiscoverFilterFields.joinedFocusDate}
            />
          </FiltersSet>
          <FiltersSet
            name="Reactions"
            isSectionDisabled={checkIfSetIsDisabled([
              DiscoverFilterFields.totalReactionsReceived,
              DiscoverFilterFields.totalPositiveReactionsReceived,
              DiscoverFilterFields.totalNegativeReactionsReceived,
              DiscoverFilterFields.totalReactionsGiven,
              DiscoverFilterFields.totalPositiveReactionsGiven,
              DiscoverFilterFields.totalNegativeReactionsGiven,
            ])}
          >
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              label="Total Reactions Received"
              name={DiscoverFilterFields.totalReactionsReceived}
              icon={<LuSmile />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              label="Total Positive Reactions Received"
              name={DiscoverFilterFields.totalPositiveReactionsReceived}
              icon={<LuThumbsUp />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalNegativeReactionsReceived}
              label="Total Negative Reactions Received"
              icon={<LuThumbsDown />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalReactionsGiven}
              label="Total Reactions Given"
              icon={<LuSmile />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalPositiveReactionsGiven}
              label="Total Positive Reactions Given"
              icon={<LuThumbsUp />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalNegativeReactionsGiven}
              label="Total Negative Reactions Given"
              icon={<LuThumbsDown />}
            />
          </FiltersSet>

          <FiltersSet
            name="Diamonds"
            isSectionDisabled={checkIfSetIsDisabled([
              DiscoverFilterFields.totalDiamondsUsdCentsReceived,
              DiscoverFilterFields.totalDiamondsUsdCentsSent,
            ])}
          >
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalDiamondsUsdCentsReceived}
              label="Total Tips Received USD"
              icon={<LuGem />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalDiamondsUsdCentsSent}
              label="Total Tips Sent USD"
              icon={<LuGem />}
            />
          </FiltersSet>

          <FiltersSet
            name="Followers"
            isSectionDisabled={checkIfSetIsDisabled([
              DiscoverFilterFields.totalFollowers,
              DiscoverFilterFields.totalWhaleFollowers,
              DiscoverFilterFields.totalFollowing,
            ])}
          >
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalFollowers}
              label="Total Followers"
              icon={<LuUser2 />}
            />
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              label="Total Whale Followers"
              name={DiscoverFilterFields.totalWhaleFollowers}
              icon={
                <Image
                  src={WHALE_ICON}
                  unoptimized
                  alt="whale"
                  {...{ width: 20, height: 20 }}
                />
              }
            />
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalFollowing}
              label="Total Following"
              icon={<LuUsers2 />}
            />
          </FiltersSet>

          <FiltersSet
            name="Wealth"
            isSectionDisabled={checkIfSetIsDisabled([
              DiscoverFilterFields.totalAssetsBalanceUsdCents,
              DiscoverFilterFields.totalLiquidAssetsBalanceUsdCents,
              DiscoverFilterFields.desoBalanceNanos,
              DiscoverFilterFields.usdcBalanceUsdCents,
              DiscoverFilterFields.usdtBalanceUsdCents,
            ])}
          >
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalAssetsBalanceUsdCents}
              label="Total Assets USD"
              icon={<LuCircleDollarSign />}
            />
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalLiquidAssetsBalanceUsdCents}
              label="Total Liquid Assets USD"
              icon={<LuCircleDollarSign />}
            />
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.desoBalanceNanos}
              label="DESO Balance"
              icon={<LuCircleDollarSign />}
            />
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.usdcBalanceUsdCents}
              label="USDC Balance"
              icon={<LuCircleDollarSign />}
            />
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.usdtBalanceUsdCents}
              label="USDT Balance"
              icon={<LuCircleDollarSign />}
            />
          </FiltersSet>
          <FiltersSet
            name="Posts"
            isSectionDisabled={checkIfSetIsDisabled([
              DiscoverFilterFields.totalPosts,
              DiscoverFilterFields.totalFocusPostViews,
              DiscoverFilterFields.totalFocusPostWhaleViews,
              DiscoverFilterFields.totalViewedFocusPosts,
            ])}
          >
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalPosts}
              label="Total Posts Created"
              icon={<LuScrollText />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalFocusPostViews}
              label="Total Post Views"
              icon={<LuScrollText />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalFocusPostWhaleViews}
              label="Total Whale Post Views"
              icon={<LuScrollText />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              label="Total Viewed Posts"
              name={DiscoverFilterFields.totalViewedFocusPosts}
              icon={<LuScrollText />}
            />
          </FiltersSet>
          <FiltersSet
            name="Earnings"
            isSectionDisabled={checkIfSetIsDisabled([
              DiscoverFilterFields.totalFocusEarningsUsdCents,
              DiscoverFilterFields.totalJoiningRewardUsdCents,
              DiscoverFilterFields.unlockedJoiningRewardUsdCents,
              DiscoverFilterFields.lockedJoiningRewardUsdCents,
              DiscoverFilterFields.totalReferralRewardsUsdCents,
              DiscoverFilterFields.unlockedReferralRewardsUsdCents,
              DiscoverFilterFields.lockedReferralRewardsUsdCents,
            ])}
          >
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalFocusEarningsUsdCents}
              label="Total Earnings USD"
              icon={<LuLandmark />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalJoiningRewardUsdCents}
              label="Total Joining Reward USD"
              icon={<LuLandmark />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.unlockedJoiningRewardUsdCents}
              label="Unlocked Joining Reward USD"
              icon={<LuLandmark />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.lockedJoiningRewardUsdCents}
              label="Locked Joining Reward USD"
              icon={<LuLandmark />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.totalReferralRewardsUsdCents}
              label="Total Referral Rewards USD"
              icon={<LuLandmark />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.unlockedReferralRewardsUsdCents}
              label="Unlocked Referral Rewards USD"
              icon={<LuLandmark />}
            />
            <FilterNumericRangeWithTimePeriod<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.lockedReferralRewardsUsdCents}
              label="Locked Referral Rewards USD"
              icon={<LuLandmark />}
            />
          </FiltersSet>
          <FiltersSet
            name="X (Twitter)"
            isSectionDisabled={checkIfSetIsDisabled([
              DiscoverFilterFields.twitterUsername,
              DiscoverFilterFields.twitterTotalFollowers,
              DiscoverFilterFields.twitterTotalFollowing,
              DiscoverFilterFields.twitterTotalTweets,
              DiscoverFilterFields.twitterIsVerified,
            ])}
          >
            <FilterString<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.twitterUsername}
              label="X (Twitter) Username"
              placeholder="Start typing a username..."
              icon={<BsTwitterX />}
            />
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.twitterTotalFollowers}
              label="X (Twitter) Followers"
              icon={<BsTwitterX />}
            />
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.twitterTotalFollowing}
              label="X (Twitter) Following"
              icon={<BsTwitterX />}
            />
            <FilterNumeric<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.twitterTotalTweets}
              label="X (Twitter) Total Tweets Created"
              icon={<BsTwitterX />}
            />
            <FilterBoolean<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.twitterIsVerified}
              label="Verified on X (Twitter)"
              icon={<BsTwitterX />}
            />
          </FiltersSet>
        </div>
        <PopoverFilterSortActions
          className="
            sticky inset-x-auto bottom-0 z-10 -mx-6 mt-4 w-auto
            rounded-none border-x-0 border-b-0 bg-card/95 backdrop-blur-sm
          "
          customSubmitButton={
            <Button variant="default" size="lg" onClick={onSubmitClick}>
              Apply
            </Button>
          }
          onResetClick={onResetClick}
        />
      </Accordion>
    </PopoverDrawerContent>
  );
};
